import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Phone } from "lucide-react";
import { trackWhatsApp } from "../utils/analytics";

const WHATSAPP_NUMBER = "34601651014";
const PHONE_NUMBER = "+34828182100";

const PAGE_CONTEXT = {
  "/": "sobre vuestras tarifas",
  "/fibra-movil": "sobre el pack Fibra + Móvil",
  "/tarifas-movil": "sobre las tarifas móviles",
  "/fibra-fijo": "sobre la fibra y el fijo",
  "/empresas": "para autónomos y empresas",
  "/tv": "sobre LiveWifi TV",
  "/internet-rural": "sobre Internet Rural / WiMAX",
  "/roaming": "sobre roaming",
  "/estudio-zona": "para comprobar la cobertura en mi zona",
  "/peninsula": "sobre las tarifas de fibra y móvil en la Península",
  "/test-velocidad": "sobre mi velocidad de conexión",
  "/estado": "sobre el estado de la red",
  "/contacto": "con vosotros",
};

const TOPICS = [
  {
    id: "info",
    icon: "💬",
    title: "Quiero información de tarifas",
    color: "#7DD500",
    msgFn: (ctx) =>
      `Hola LiveWifi! 👋 Me gustaría recibir información ${ctx}.`,
  },
  {
    id: "coverage",
    icon: "📡",
    title: "Comprobar cobertura",
    color: "#00D4FF",
    msgFn: () =>
      `Hola LiveWifi! 👋 Quiero saber si tenéis cobertura en mi dirección. Os envío mi ubicación.`,
  },
  {
    id: "change",
    icon: "🔄",
    title: "Quiero ser cliente",
    color: "#B980F0",
    msgFn: () =>
      `Hola LiveWifi! 👋 Quiero ser cliente de LiveWifi y me gustaría conocer vuestras tarifas.`,
  },
  {
    id: "support",
    icon: "🛠️",
    title: "Soporte / avería",
    color: "#FF9800",
    msgFn: () =>
      `Hola LiveWifi! 👋 Tengo un problema con mi conexión y necesito ayuda.`,
  },
  {
    id: "billing",
    icon: "🧾",
    title: "Dudas sobre mi factura",
    color: "#FFC400",
    msgFn: () =>
      `Hola LiveWifi! 👋 Tengo una consulta sobre mi factura.`,
  },
];

const buildWaUrl = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const FloatingWhatsApp = () => {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);
  const location = useLocation();
  const containerRef = useRef(null);

  // Pulse solo la primera vez que carga
  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 8000);
    return () => clearTimeout(t);
  }, []);

  // Cerrar al cambiar de página
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const ctx = PAGE_CONTEXT[location.pathname] || "sobre vuestros servicios";

  // On mobile-only tariff page, hide the "Comprobar cobertura" topic (no fiber relevant)
  const hideCoverageTopic = location.pathname === "/tarifas-movil";
  const visibleTopics = hideCoverageTopic ? TOPICS.filter((t) => t.id !== "coverage") : TOPICS;

  const handleTopic = (topic) => {
    trackWhatsApp();
    window.open(buildWaUrl(topic.msgFn(ctx)), "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className="fixed bottom-6 right-6 z-[60]"
      data-testid="floating-whatsapp"
    >
      {/* Panel expandido */}
      {open && (
        <div
          className="mb-4 w-[320px] sm:w-[360px] rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] border border-white/10"
          style={{ animation: "wa-slide-up 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          data-testid="floating-whatsapp-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#075E54] to-[#128C7E] p-4 relative">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-white/70 hover:text-white"
              data-testid="floating-whatsapp-close"
              aria-label="Cerrar chat"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center text-2xl border-2 border-white/30">
                  💬
                </div>
                <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-[#7DD500] border-2 border-[#075E54]" />
              </div>
              <div>
                <div className="text-white font-bold text-base">Equipo LiveWifi</div>
                <div className="text-white/70 text-xs">Respondemos en minutos</div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="bg-[#0A0A0A] p-4 max-h-[70vh] overflow-y-auto">
            <p className="text-white/70 text-xs mb-3 leading-relaxed">
              Hola 👋 ¿En qué podemos ayudarte? Elige un tema y abriremos
              WhatsApp con el mensaje preparado.
            </p>
            <div className="space-y-2">
              {visibleTopics.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleTopic(t)}
                  data-testid={`floating-whatsapp-topic-${t.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.07] border border-white/5 hover:border-white/20 transition-all text-left group"
                >
                  <span
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
                    style={{ backgroundColor: `${t.color}15` }}
                  >
                    {t.icon}
                  </span>
                  <span className="flex-1 text-white text-sm font-medium">
                    {t.title}
                  </span>
                  <span
                    className="text-xs opacity-0 group-hover:opacity-100 transition"
                    style={{ color: t.color }}
                  >
                    →
                  </span>
                </button>
              ))}
            </div>

            {/* Botón llamada */}
            <a
              href={`tel:${PHONE_NUMBER}`}
              data-testid="floating-whatsapp-call"
              className="mt-3 flex items-center justify-center gap-2 w-full p-3 rounded-xl border border-white/10 text-white/80 text-xs font-medium hover:bg-white/5 transition"
            >
              <Phone className="w-4 h-4 text-[#7DD500]" />
              <span>o llámanos al {PHONE_NUMBER}</span>
            </a>

            <p className="text-center text-[10px] text-gray-600 mt-3">
              Horario: Lun–Vie 9:00–20:00 · Sáb 10:00–14:00
            </p>
          </div>
        </div>
      )}

      {/* Botón principal */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="whatsapp-float-btn"
        aria-label={open ? "Cerrar chat" : "Abrir chat de WhatsApp"}
        className="relative w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition-transform hover:shadow-[0_0_30px_rgba(37,211,102,0.6)]"
      >
        {pulse && !open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping" />
        )}
        {open ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <MessageCircle className="w-8 h-8 text-white" fill="white" />
        )}
        {!open && pulse && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-[#0A0A0A]">
            1
          </span>
        )}
      </button>

      <style>{`
        @keyframes wa-slide-up {
          from { opacity: 0; transform: translateY(16px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default FloatingWhatsApp;
